import { AppModule } from 'app/module';
import { KEYCODE_ENTER, KEYCODE_ESCAPE } from 'app/keyCodes';
import { mediaQueries } from 'util/mediaQueries';
import { Checkbox } from 'module/checkbox/main';
import { smoothScrollInsideElement } from 'util/scroll';
import { handleBounce } from 'util/bounce';
import Template from './template.hbs';
import './styles.scss';

let yCoordinate = 0;

export class GdprModal extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    domBindings() {
        return {
            contentInner: '.m-gdprModal__contentInner',
            inner: '.m-gdprModal__inner',
            headline: '.m-gdprModal__headline',
            gdprCompanyBox: '.m-gdprModal__gdprCompanyBox',
            gdprOptionalBox: '.m-gdprModal__gdprOptionalBox',
            gdprRequiredBox: '.m-gdprModal__gdprRequiredBox',
            acceptButton: '.m-gdprModal__acceptGdpr',
            closeTrigger: '.m-gdprModal__closeTrigger',
            closeButton: '.m-gdprModal__close',
            optionalCheckBoxes: ['.m-gdprModal__gdprOptionalItem .m-checkbox'],
            requiredCheckBoxContainers: ['.m-gdprModal__gdprRequiredBoxItem'],
            requiredCheckBoxes: ['.m-gdprModal__gdprRequiredBoxItem .m-checkbox'],
            requiredBoxErrorMessage: '.m-gdprModal__gdprRequiredBoxItemErrorMessage',
            overlay: '.m-gdprModal__overlay',
            insideModal: '.m-gdprModal__overlay--insideModal',
        };
    }

    subs() {
        if (this.dom.optionalCheckBoxes) {
            this.optionalCheckBoxes = [];

            for (let i = 0; i < this.dom.optionalCheckBoxes.length; i += 1) {
                this.optionalCheckBoxes[i] = new Checkbox(this.dom.optionalCheckBoxes[i]);
            }
        }

        if (this.dom.requiredCheckBoxes) {
            this.requiredCheckBoxes = [];

            for (let i = 0; i < this.dom.requiredCheckBoxes.length; i += 1) {
                this.requiredCheckBoxes[i] = new Checkbox(this.dom.requiredCheckBoxes[i]);
            }
        }
    }

    subsProps() {
        if (this.optionalCheckBoxes && this.optionalCheckBoxes.length > 0) {
            this.props.optionalBox.items = this.optionalCheckBoxes.map((optionalBox) => optionalBox.getPropsFromDom());
        }

        if (this.requiredCheckBoxes && this.requiredCheckBoxes.length > 0) {
            this.props.requiredBox.items = this.requiredCheckBoxes.map((requiredBox) => requiredBox.getPropsFromDom());
        }
    }

    validate() {
        if (this.requiredCheckBoxes) {
            const validationErrors = [];
            for (let i = 0; i < this.requiredCheckBoxes.length; i += 1) {
                const requiredEmailErrors = this.requiredCheckBoxes[i].validate(['required']);

                if (requiredEmailErrors.length === 0) {
                    this.dom.requiredCheckBoxContainers[i].classList.remove('m-gdprModal__gdprRequiredBoxItem--error');
                    this.dom.requiredBoxErrorMessage.classList
                        .add('m-gdprModal__gdprRequiredBoxItemErrorMessage--hidden');
                } else {
                    this.dom.requiredBoxErrorMessage.classList
                        .remove('m-gdprModal__gdprRequiredBoxItemErrorMessage--hidden');
                    this.dom.requiredCheckBoxContainers[i].classList.add('m-gdprModal__gdprRequiredBoxItem--error');
                }

                validationErrors.push(requiredEmailErrors);
            }

            const isValid = validationErrors.filter((x) => x.length).length === 0;
            this.dom.acceptButton.disabled = isValid;

            return isValid;
        }

        return true;
    }

    domEvents() {
        this.dom.el.addEventListener('keydown', (e) => this.pressKey(e.key));

        if (!this.dom.insideModal) {
            if (!mediaQueries.isMobile() && this.dom.inner) {
                this.dom.inner.addEventListener('scroll', () => this.checkIfScrollEnd());

                this.dom.inner.addEventListener('touchstart', (e) => {
                    yCoordinate = e.touches[0].clientY;
                });

                this.dom.inner.addEventListener('touchmove', (e) => {
                    e.stopPropagation();
                    yCoordinate = handleBounce(e, yCoordinate);
                });
            }
            if (mediaQueries.isMobile() && this.dom.contentInner) {
                this.dom.contentInner.addEventListener('touchstart', (e) => {
                    yCoordinate = e.touches[0].clientY;
                });

                this.dom.contentInner.addEventListener('touchmove', (e) => {
                    e.stopPropagation();
                    yCoordinate = handleBounce(e, yCoordinate);
                });
            }
        }
        if (this.dom.acceptButton) {
            this.dom.acceptButton.addEventListener('click', () => this.acceptGdpr());
        }
        if (this.dom.closeTrigger) {
            this.dom.closeTrigger.addEventListener('click', () => this.closeModal());
        }
        if (this.dom.closeButton) {
            this.dom.closeButton.addEventListener('click', () => this.closeModal());
        }
        if (this.dom.overlay) {
            this.dom.overlay.addEventListener('click', () => this.closeModal());
        }
    }

    subEvents() {
        if (this.props.active && this.optionalCheckBoxes) {
            for (let i = 0; i < this.optionalCheckBoxes.length; i += 1) {
                this.optionalCheckBoxes[i].events.on('toggle', () => {
                    this.props.optionalBox.items[i].checked = this.optionalCheckBoxes[i].props.checked;
                });
            }
            for (let i = 0; i < this.requiredCheckBoxes.length; i += 1) {
                this.requiredCheckBoxes[i].events.on('toggle', () => {
                    this.props.requiredBox.items[i].checked = this.requiredCheckBoxes[i].props.checked;
                });
            }
        }
    }

    autoScrollOneFrame() {
        if (!mediaQueries.isMobile()) {
            const scrollingOverlapThreshold = 20;
            const modalScrollHeight = this.dom.inner.clientHeight - scrollingOverlapThreshold;
            smoothScrollInsideElement(
                this.dom.inner,
                this.props.scrollTop,
                this.props.scrollTop + modalScrollHeight,
                1000,
            );
        }
    }

    checkIfScrollEnd() {
        if (!mediaQueries.isMobile()) {
            this.props.scrollTop = this.dom.inner.scrollTop;
            const scrollingThreshold = 1;
            const isScrollEnd = Math.abs(this.dom.inner.scrollHeight
                - this.dom.inner.scrollTop - this.dom.inner.clientHeight) <= scrollingThreshold;

            if (isScrollEnd && this.props.read !== true) {
                this.enableButtons();
            }
        }
    }

    enableButtons() {
        this.updateProps({ read: true }, true);
        this.dom.inner.scrollTop = this.dom.inner.scrollHeight;
    }

    acceptGdpr() {
        this.dom.acceptButton.disabled = true;
        this.events.emit('acceptGdpr');
    }

    closeModal() {
        this.updateProps({ visible: false }, true);
        this.events.emit('removeModalStyle');
        if (this.props.insideModal) {
            this.events.emit('updateIFrame');
        }
    }

    getPropsFromDom() {
        return {
            active: !!this.dom.overlay,
            visible: !this.dom.el.classList.contains('m-gdprModal--hide'),
            headline: !!this.dom.headline,
            insideModal: !!this.dom.insideModal,
            companyBox: {
                active: !!this.dom.gdprCompanyBox,
            },
            optionalBox: {
                active: !!this.dom.gdprOptionalBox,
            },
            requiredBox: {
                active: !!this.dom.gdprRequiredBox,
            },
        };
    }

    pressKey(keyCode) {
        if ((keyCode === KEYCODE_ENTER && document.activeElement === this.dom.closeTrigger)
            || keyCode === KEYCODE_ESCAPE) {
            this.closeModal();
        }
    }
}
